import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faThLarge,
  faNetworkWired,
  faCircleInfo,
  faBars,
  faRightFromBracket,
  faPlus,
  faCircle,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import hubIcon from "../../assets/hub-1.svg";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedGateway } from "../../redux/slices/gatewaysSlice";
import { getTranslation } from "../../translations/getTranslations";
import useMobilePWAInstall from "../../services/useMobilePWAInstall";

const VerticalNavbar = ({
  isMobile,
  onSignOut,
  onNavItemSelect,
  isNavbarCollapsed,
  toggleNavbarCollapse,
  isToggleButtonHidden,
}) => {
  const [showGateways, setShowGateways] = useState(false);
  const activeComponent = useSelector(state => state.auth.activeComponent);
  const gateways = useSelector(state => state.gateways.data);
  const currentUsername = useSelector(state => state.auth.user.fullName);
  const selectedGateway = useSelector(state => state.gateways.selectedGateway);
  const language = useSelector(state => state.settings.language);
  const token = useSelector(state => state.auth.user.token);

  const showInstructions = useMobilePWAInstall();
  const menuRef = useRef(null);
  const isPWA = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

  const dispatch = useDispatch();
  const translation = key => getTranslation("navbar", key, language);

  const handleNavItemSelect = item => {
    onNavItemSelect(item);
    if (item === "ManageGateways") {
      dispatch(setSelectedGateway([]));
      setShowGateways(true);
    } else {
      setShowGateways(false);
    }

    if (isMobile && item !== "ManageGateways") {
      toggleNavbarCollapse();
    }
  };

  const handleGatewaySelect = async gateway => {
    dispatch(setSelectedGateway(gateway));
    onNavItemSelect("Hosts");
    if (isMobile) {
      toggleNavbarCollapse();
    }
    if (!token) return;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        if (!isNavbarCollapsed && isMobile) {
          toggleNavbarCollapse();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNavbarCollapsed, isMobile, toggleNavbarCollapse]);

  return (
    <nav
      ref={menuRef}
      className={`fixed left-0 top-0 z-50 h-full bg-backgroundSecondary transition-all duration-300 ease-in-out ${isNavbarCollapsed ? "w-0" : "w-[240px]"} `}
    >
      <button
        onClick={toggleNavbarCollapse}
        className={`fixed left-1 top-1 z-50 rounded p-2 ${
          isNavbarCollapsed ? `bg-backgroundPrimary text-textPrimary` : `bg-backgroundSecondary text-textSecondary`
        } ${isToggleButtonHidden ? "hidden" : ""}`}
      >
        <FontAwesomeIcon icon={faBars} className="text-xl" />
      </button>

      <div
        className={`flex h-full flex-col items-center overflow-y-auto p-4 transition-all duration-300 ease-in-out ${isNavbarCollapsed ? "pointer-events-none opacity-0" : "pointer-events-auto opacity-100"}`}
      >
        <div className={`my-10 flex flex-col items-center justify-center`}>
          <span className={`bg-clip-text text-center text-2xl font-bold uppercase text-textSecondary`}>
            Olympia Gateway
          </span>
          <img src={hubIcon} alt="Hub Icon" className={`my-2 h-10 w-10`} />
          <span className={`bg-clip-text text-2xl font-bold uppercase text-textSecondary`}>Dashboard</span>
        </div>

        <p className={`mb-5 text-center text-sm text-textSecondary`}>
          {translation("_welcome")}, {currentUsername}
        </p>

        <hr className={`my-5 h-px w-full border-none bg-highlightColor`} />

        <div className="mb-5 flex w-full flex-col">
          <button
            className={`flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-lg hover:bg-highlightColor hover:text-textPrimary ${
              activeComponent === "AddGateway" ? `bg-highlightColor text-textPrimary` : `text-textSecondary`
            } mb-2.5`}
            onClick={() => handleNavItemSelect("AddGateway")}
          >
            <FontAwesomeIcon icon={faPlus} style={{ width: "25px" }} />
            {translation("_addGateway")}
          </button>

          <button
            className={`flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-lg hover:bg-highlightColor hover:text-textPrimary ${
              activeComponent === "ManageGateways" || activeComponent === "Hosts"
                ? `bg-highlightColor text-textPrimary`
                : `text-textSecondary`
            } mb-2.5`}
            onClick={() => handleNavItemSelect("ManageGateways")}
          >
            <FontAwesomeIcon icon={faThLarge} style={{ width: "25px" }} />
            {translation("_gateways")}
          </button>

          <div
            className={`flex w-full flex-col gap-2 overflow-hidden text-base transition-all duration-500 ${
              showGateways && (activeComponent === "ManageGateways" || activeComponent === "Hosts")
                ? "mb-2.5 max-h-[400px]"
                : "max-h-0"
            }`}
          >
            {gateways.map((gateway, index) => (
              <button
                key={index}
                className={`ml-5 flex cursor-pointer items-center gap-2 rounded p-2 ${
                  selectedGateway.name === gateway.name ? `bg-highlightColor text-textPrimary` : `text-textSecondary`
                } hover:bg-highlightColor hover:text-textPrimary`}
                onClick={() => handleGatewaySelect(gateway)}
              >
                <FontAwesomeIcon icon={faNetworkWired} style={{ width: "25px" }} />
                {gateway.name}
                <FontAwesomeIcon
                  icon={faCircle}
                  style={{ width: "15px", height: "15px" }}
                  className={`${gateway.gatewayStatus === true ? "animate-pulse text-successStatus" : "text-neutralStatus"}`}
                />
              </button>
            ))}
          </div>

          <button
            className={`flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-lg hover:bg-highlightColor hover:text-textPrimary ${
              activeComponent === "Settings" ? `bg-highlightColor text-textPrimary` : `text-textSecondary`
            } mb-2.5`}
            onClick={() => handleNavItemSelect("Settings")}
          >
            <FontAwesomeIcon icon={faCog} style={{ width: "25px" }} />
            {translation("_settings")}
          </button>

          <button
            className={`flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-lg hover:bg-highlightColor hover:text-textPrimary ${
              activeComponent === "About" ? `bg-highlightColor text-textPrimary` : `text-textSecondary`
            }`}
            onClick={() => handleNavItemSelect("About")}
          >
            <FontAwesomeIcon icon={faCircleInfo} style={{ width: "25px" }} />
            {translation("_about")}
          </button>
        </div>

        <hr className={`my-5 h-px w-full border-none bg-highlightColor`} />
        {isMobile && !isPWA && (
          <button
            onClick={showInstructions}
            className="flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-left text-lg text-textSecondary hover:bg-highlightColor hover:text-textPrimary"
          >
            <FontAwesomeIcon icon={faDownload} style={{ width: "25px" }} />
            {translation("_installApp")}
          </button>
        )}
        <button
          className={`mt-auto flex w-full cursor-pointer items-center gap-2 rounded border-none p-2.5 text-left text-lg text-textSecondary hover:bg-highlightColor hover:text-textPrimary`}
          onClick={onSignOut}
        >
          <FontAwesomeIcon icon={faRightFromBracket} style={{ width: "25px" }} />
          {translation("_logout")}
        </button>
      </div>
    </nav>
  );
};

export default VerticalNavbar;
